import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from "chart.js";
import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { LouiseBadgeIcon } from "../../../../components/common/icons/louiseBadge.icon";
import { PARTNER_LEVEL } from "../../../../utils/partnerObject";

import { commissions } from "../../../../utils/affiliateCommissions";
import HintBlock from "../../../../components/common/HintBlock"; // Chart.js registration

// Chart.js registration
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Props {
  simulationSales: number;
  level:
    | PARTNER_LEVEL.STANDARD
    | PARTNER_LEVEL.BRONZE
    | PARTNER_LEVEL.SILVER
    | PARTNER_LEVEL.GOLD;
  nextLevel:
    | PARTNER_LEVEL.STANDARD
    | PARTNER_LEVEL.BRONZE
    | PARTNER_LEVEL.SILVER
    | PARTNER_LEVEL.GOLD;
}

const CommissionSimulatorBlock: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [sales, setSales] = useState<number>(props.simulationSales ?? 50);
  const [simulatationLevel, setSimulatationLevel] = useState<
    | PARTNER_LEVEL.STANDARD
    | PARTNER_LEVEL.BRONZE
    | PARTNER_LEVEL.SILVER
    | PARTNER_LEVEL.GOLD
  >(props.nextLevel);
  const [results, setResults] = useState<any>(null);

  const MIN_SUBSCRIPTION = commissions.plans.min_price_plan;
  const MAX_SUBSCRIPTION = commissions.plans.max_price_plan;

  const calculateAffiliateEarnings = (sales: number): any => {
    const selectedLevel = commissions.by_affiliate[simulatationLevel];

    if (!selectedLevel) {
      return { error: t("BusinessPartner/SimulateurAffiliation/error") };
    }

    const oneShotMin =
      sales * MIN_SUBSCRIPTION * selectedLevel.oneShot_commission;
    const recurringMin =
      sales * MIN_SUBSCRIPTION * selectedLevel.recurring_commission;

    const oneShotMax =
      sales * MAX_SUBSCRIPTION * selectedLevel.oneShot_commission;
    const recurringMax =
      sales * MAX_SUBSCRIPTION * selectedLevel.recurring_commission;

    return {
      level: selectedLevel.name,
      sales,
      oneShotMin: oneShotMin.toFixed(2),
      recurringMin: recurringMin.toFixed(2),
      oneShotMax: oneShotMax.toFixed(2),
      recurringMax: recurringMax.toFixed(2),
      message: `${t("BusinessPartner/SimulateurAffiliation/levelDescription")}${
        selectedLevel.name
      }${t(
        "BusinessPartner/SimulateurAffiliation/salesDescription"
      )}${sales}${t(
        "BusinessPartner/SimulateurAffiliation/earningsDescription"
      )}${t("BusinessPartner/SimulateurAffiliation/from")}${oneShotMin}$ (${t(
        "BusinessPartner/SimulateurAffiliation/recurring"
      )} ${recurringMin}$${t(
        "BusinessPartner/SimulateurAffiliation/monthly"
      )}) ${t("BusinessPartner/SimulateurAffiliation/to")}${oneShotMax}$ (${t(
        "BusinessPartner/SimulateurAffiliation/recurring"
      )} ${recurringMax}$${t(
        "BusinessPartner/SimulateurAffiliation/annually"
      )}).`
    };
  };

  const handleCalculate = () => {
    const result = calculateAffiliateEarnings(sales);
    setResults(result);
  };

  const minChartData = results
    ? {
        labels: [
          t("BusinessPartner/SimulateurAffiliation/commissionsJ15"),
          t("BusinessPartner/SimulateurAffiliation/recurringCommissions")
        ],
        datasets: [
          {
            label: t("BusinessPartner/SimulateurAffiliation/minimumEarnings"),
            data: [results.oneShotMin, results.recurringMin],
            backgroundColor: "rgba(27,182,77,0.85)"
          }
        ]
      }
    : null;

  const maxChartData = results
    ? {
        labels: [
          t("BusinessPartner/SimulateurAffiliation/commissionsJ15"),
          t("BusinessPartner/SimulateurAffiliation/recurringCommissions")
        ],
        datasets: [
          {
            label: t("BusinessPartner/SimulateurAffiliation/maximumEarnings"),
            data: [results.oneShotMax, results.recurringMax],
            backgroundColor: "rgb(37,99,235)"
          }
        ]
      }
    : null;

  return (
    <React.Fragment>
      <div className="tw-w-full tw-px-6 tw-pt-4 tw-pb-6 tw-bg-white tw-rounded-2xl tw-shadow">
        {/* Header Section */}
        <div className="tw-grid tw-gap-4">
          <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("BusinessPartner/SimulateurAffiliation/earningsSimulator")}
          </div>
          <div className="tw-flex tw-px-5 tw-py-2.5 tw-bg-gray-100 tw-rounded-[99px] tw-justify-between tw-items-center">
            <div className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend']">
              {t("BusinessPartner/SimulateurAffiliation/actual")}
            </div>
            <LouiseBadgeIcon theme={props.level} width="25px" height="25px" />
          </div>
        </div>

        {/* Input Section */}
        <div className="tw-grid tw-gap-6 tw-mt-6">
          {/* Select Level */}
          <div className="tw-grid tw-gap-2">
            <label
              htmlFor="level"
              className="tw-text-[#687076] tw-text-xs tw-font-normal tw-leading-none"
            >
              {t("BusinessPartner/SimulateurAffiliation/level")}
            </label>
            <div className="tw-self-stretch tw-pl-[17px] tw-pr-[13px] tw-py-[9px] tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex">
              <select
                id="level"
                value={simulatationLevel}
                onChange={(e) =>
                  setSimulatationLevel(
                    e.target.value as
                      | PARTNER_LEVEL.STANDARD
                      | PARTNER_LEVEL.BRONZE
                      | PARTNER_LEVEL.SILVER
                      | PARTNER_LEVEL.GOLD
                  )
                }
                className="tw-grow tw-outline-none tw-ring-0 tw-border-none tw-bg-transparent tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              >
                <option value={PARTNER_LEVEL.STANDARD}>
                  {t(PARTNER_LEVEL.STANDARD)}{" "}
                  {t("BusinessPartner/SimulateurAffiliation/standardCondition")}
                </option>
                <option value={PARTNER_LEVEL.BRONZE}>
                  {t(PARTNER_LEVEL.BRONZE)}{" "}
                  {t("BusinessPartner/SimulateurAffiliation/bronzeCondition")}
                </option>
                <option value={PARTNER_LEVEL.SILVER}>
                  {t(PARTNER_LEVEL.SILVER)}{" "}
                  {t("BusinessPartner/SimulateurAffiliation/silverCondition")}
                </option>
                <option value={PARTNER_LEVEL.GOLD}>
                  {t(PARTNER_LEVEL.GOLD)}{" "}
                  {t("BusinessPartner/SimulateurAffiliation/goldCondition")}
                </option>
              </select>
            </div>
            <HintBlock
              texts={[
                t("BusinessPartner/SimulateurAffiliation/permanentUnlock")
              ]}
            />
          </div>

          <div className="tw-grid tw-gap-2">
            <label
              htmlFor="sales"
              className="tw-text-[#687076] tw-text-xs tw-font-normal"
            >
              {t("BusinessPartner/SimulateurAffiliation/numberOfSales")}
            </label>
            <div className="tw-self-stretch tw-pl-[17px] tw-pr-[13px] tw-py-[9px] tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex">
              <input
                id="sales"
                type="number"
                defaultValue={sales}
                onChange={(e) => setSales(Number(e.target.value))}
                className="tw-grow tw-outline-none tw-ring-0 tw-border-none tw-bg-transparent tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              />
            </div>
          </div>
          <button
            onClick={handleCalculate}
            className=" tw-border-none tw-px-[21px] tw-py-[7px] tw-bg-primary tw-rounded-[99px] tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend']"
          >
            {t("BusinessPartner/SimulateurAffiliation/calculateEarnings")}
          </button>
        </div>

        {/* Results Section */}
        {results && (
          <div className="tw-mt-6">
            {!isDesktop ? (
              <div className="tw-text-center tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-shadow-inner">
                <p className="tw-text-lg tw-font-bold tw-text-gray-700 tw-m-0">
                  {t("BusinessPartner/SimulateurAffiliation/onlyDesktop")}
                </p>
                <hr />
                <p className="tw-mt-4 tw-text-gray-700">{results.message}</p>
              </div>
            ) : (
              <div className="tw-grid tw-gap-6">
                {/* Minimum Earnings Chart */}
                <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-shadow-inner">
                  <h2 className="tw-text-lg tw-font-bold tw-text-gray-700">
                    {t("BusinessPartner/SimulateurAffiliation/minimumEarnings")}
                  </h2>
                  <Bar
                    data={minChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" as const },
                        title: {
                          display: true,
                          text: t(
                            "BusinessPartner/SimulateurAffiliation/minEarningsExplanation"
                          )
                        }
                      },
                      scales: {
                        y: { beginAtZero: true }
                      }
                    }}
                  />
                </div>
                {/* Maximum Earnings Chart */}
                <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-shadow-inner">
                  <h2 className="tw-text-lg tw-font-bold tw-text-gray-700">
                    {t("BusinessPartner/SimulateurAffiliation/maximumEarnings")}
                  </h2>
                  <Bar
                    data={maxChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" as const }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default CommissionSimulatorBlock;
